import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import AppProviders from '../../AppProviders';
import { FullPageDrawer } from '../../components/Drawer/Drawer';
import SamlProtectedRoute from '../../components/SamlProtectedRoute';
import type { MetaResolver } from '../../helpers/head-manager';
import ChangeAdminPermissions from '../ChangeAdminPermissions/ChangeAdminPermissions';
import { CompaniesPage } from '../Companies/CompaniesPage';
import { CompanyPage } from '../Company/Company';
import { CompanyDetailsPage } from '../Company/CompanyDetails/CompanyDetailsPage';
import { CompanyEntitiesPage } from '../Company/CompanyEntities/CompanyEntitiesPage';
import { CompanyTriggersPage } from '../Company/CompanyTriggers/CompanyTriggersPage';
import { CompanyUsersPage } from '../Company/CompanyUsers/CompanyUsersPage';
import DeleteInstance from '../DeleteInstance/DeleteInstance';
import IntacctAdmin from '../Home';
import MultiEntity from '../MultiEntity';
import NotFoundPage from '../NotFoundPage';
import Provision from '../Provision';
import ProvisionTest from '../ProvisionTest/ProvisionTest';
import QueuesPage from '../Queues';
import ResendInvite from '../ResendInvite/ResendInvite';
import ResetMfa from '../ResetMfa/ResetMfa';
import RunAnyJobForm from '../RunJobs/RunAnyJob';
import Saml from '../Saml';
import SamlCallback from '../Saml/SamlCallback';
import SamlLogin from '../Saml/SamlLogin';
import { UserPage } from '../User/UserPage';

const Root = () => {
  return (
    <AppProviders>
      <FullPageDrawer />
    </AppProviders>
  );
};

const router = createBrowserRouter([
  {
    element: <Root />,
    handle: {
      meta: {
        title: 'Admin Portal | Sage Intelligent Time',
      },
    } satisfies MetaResolver,
    children: [
      {
        path: '/saml',
        element: <Saml />,
        handle: {
          meta: {
            title: 'SAML | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
        children: [
          {
            path: '',
            element: <SamlLogin />,
          },
          {
            path: 'callback',
            element: <SamlCallback />,
          },
          {
            path: '*',
            element: <Navigate to="/" />,
          },
        ],
      },
      {
        path: '/',
        index: true,
        element: (
          <SamlProtectedRoute>
            <IntacctAdmin />
          </SamlProtectedRoute>
        ),
      },
      {
        path: '/companies',
        element: (
          <SamlProtectedRoute>
            <CompaniesPage />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Companies | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/companies/:id',
        element: (
          <SamlProtectedRoute>
            <CompanyPage />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Company Details | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
        children: [
          {
            path: '',
            index: true,
            element: <CompanyDetailsPage />,
          },
          {
            path: 'entities',
            element: <CompanyEntitiesPage />,
          },
          {
            path: 'triggers',
            element: <CompanyTriggersPage />,
          },
          {
            path: 'users',
            element: <CompanyUsersPage />,
          },
        ],
      },
      {
        path: '/delete-instance',
        element: (
          <SamlProtectedRoute>
            <DeleteInstance />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Delete Instance | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/provision',
        element: (
          <SamlProtectedRoute>
            <Provision />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Provision | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/provision-test',
        element: (
          <SamlProtectedRoute>
            <ProvisionTest />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Provision Test | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/resend-invite',
        element: (
          <SamlProtectedRoute>
            <ResendInvite />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Resend Invite | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/reset-mfa',
        element: (
          <SamlProtectedRoute>
            <ResetMfa />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Reset MFA | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/multi-entity',
        element: (
          <SamlProtectedRoute>
            <MultiEntity />
          </SamlProtectedRoute>
        ),
      },
      {
        path: '/grant-admin-permissions',
        element: (
          <SamlProtectedRoute>
            <ChangeAdminPermissions makeAdmin />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Grant Admin Permissions | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/queues',
        element: (
          <SamlProtectedRoute>
            <QueuesPage />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Queues | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/revoke-admin-permissions',
        element: (
          <SamlProtectedRoute>
            <ChangeAdminPermissions />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Revoke Admin Permissions | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/run-jobs',
        element: (
          <SamlProtectedRoute>
            <RunAnyJobForm />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'Run Jobs | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '/users/:id',
        element: (
          <SamlProtectedRoute>
            <UserPage />
          </SamlProtectedRoute>
        ),
        handle: {
          meta: {
            title: 'User Details | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
      {
        path: '*',
        element: <NotFoundPage />,
        handle: {
          meta: {
            title: 'Not Found | Sage Intelligent Time',
          },
        } satisfies MetaResolver,
      },
    ],
  },
]);

const AppRouter = () => <RouterProvider router={router} />;

export default AppRouter;
